import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from "react";
import { GridSortModel } from "@mui/x-data-grid";
import DataGrid, { DataGridProps, DataGridRow } from "./DynamicDatagridDisplay";
import { RequestFilter } from "../../Redux/Api/Request/DynamicListRequest";
import DataGridNoRowsOverlay from "./DatagridLayout/DataGridNoRowsOverlay";

type ApiSortModel = {
    field: string;
    sort: string;
}[];

const transformSortModel = (gridSort: GridSortModel): ApiSortModel => {
    return gridSort
        .filter(item => item.sort !== null && item.sort !== undefined)
        .map(item => ({
            field: item.field,
            sort: item.sort as string
        }));
};

type DynamicDataGridProps = Omit<DataGridProps, "rows"> & {
    organisation: string;
    filter?: RequestFilter;
    queryHook: any;
    onRowClick?: (params: DataGridRow & { field: string }) => void;
    disableHeaderCells?: string[];
    totalCount?: number;
};

export type DynamicDataGridHandle = {
    reload: () => void;
};

const DynamicDataGrid: ForwardRefRenderFunction<DynamicDataGridHandle, DynamicDataGridProps> = ({
    filter,
    organisation,
    queryHook,
    onRowClick,
    disableHeaderCells,
    totalCount,
    ...props
}, ref) => {
    const [page, setPage] = useState<number>(props.page || 0);
    const [pageSize, _setPageSize] = useState<number>(25);
    const [sort, setSort] = useState<GridSortModel>(props.sortModel || [{ field: 'createdAt', sort: 'desc' }]);

    const { data, isFetching, refetch } = queryHook({
        id: organisation,
        filter,
        page: (page + 1),
        pageSize,
        sort: transformSortModel(sort)
    });

    useImperativeHandle(ref, () => ({
        reload() {
            refetch();
        }
    }));

    useEffect(() => {
        if (sessionStorage.getItem("cardChanged") === "true") {
            sessionStorage.removeItem("cardChanged");
            refetch();
        }
    }, [refetch]);

    const rowcount = data ? data.totalItems : 0;
    let rows: object[] = [];

    if (!isFetching && data) {
        rows = data.cards;
        if (!rows || rowcount <= 0) {
            return <DataGridNoRowsOverlay filtered={Array.isArray(filter) && filter.length > 0} className={props.className} />;
        }
    }

    return (
        <DataGrid
            loading={isFetching}
            filtered={Array.isArray(filter) && filter.length > 0}
            paginationMode="server"
            sortingMode="server"
            onPageChange={(page: number) => setPage(page)}
            onSortModelChange={setSort}
            rowCount={data ? data.totalItems : 0}
            rows={rows}
            data={(data ? data.id ?? 0 : 0) as unknown as (string | number)[]}
            onRowClick={onRowClick}
            disableHeaderCells={disableHeaderCells}
            {...props}
        />
    );
};

export default forwardRef(DynamicDataGrid);
